import { useState } from 'react'
import { Bot, ChevronDown, ChevronRight, Cog, KeyRound, Plug, UserRound, type LucideIcon, MailOpen } from 'lucide-react'
import { AdminIcon, FacilityAdministratorIcon, OperatorIcon, RootAdminIcon, SalesIcon, WorkerIcon } from '../../core/index.js'
import type { ConfigChange, ConfigChangeActorKind } from '../types.js'
import { ACTOR_KIND_LABEL, actorName, formatValue, headline } from './configChangeText.js'

const ACTOR_ICON: Record<ConfigChangeActorKind, LucideIcon | typeof OperatorIcon> = {
  root: RootAdminIcon,
  admin: AdminIcon,
  sales: SalesIcon,
  operator: OperatorIcon,
  facility_administrator: FacilityAdministratorIcon,
  worker: WorkerIcon,
  partner: Plug,
  reset_link: KeyRound,
  invitation: MailOpen,
  anonymous: UserRound,
  system: Cog,
}

interface ConfigChangeRowProps {
  change: ConfigChange
  onSubject: (change: ConfigChange) => void
  onActor: (change: ConfigChange) => void
  onClient: (change: ConfigChange) => void
}

/** One change: when, who, what, and - opened - each field before and after. */
export function ConfigChangeRow({ change, onSubject, onActor, onClient }: ConfigChangeRowProps) {
  const [open, setOpen] = useState(false)
  const Icon = ACTOR_ICON[change.actor.kind]
  const time = new Date(change.at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' })
  const fields = change.changes.length
  return (
    <li className="border-b border-marine/10 last:border-b-0">
      <div className="flex items-start gap-3 px-4 py-3">
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={open ? 'Hide the fields' : 'Show the fields'} className="mt-0.5 text-marine">
          {open ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
        </button>
        <div className="w-20 shrink-0 pt-0.5 text-[11px] text-ink-mid">{time}</div>
        <div className="min-w-0 flex-1">
          <div className="text-xs text-ink">
            <span className="font-bold">{headline(change)}</span>
            {' · '}
            <button type="button" onClick={() => onSubject(change)} title="This record's history" className="underline decoration-marine/30 underline-offset-2 hover:text-marine">
              {change.subjectLabel ?? change.subjectId}
            </button>
            {change.organizationName && change.organizationName !== (change.subjectLabel ?? change.subjectId) && (
              <>
                {' · '}
                <button type="button" onClick={() => onClient(change)} title="Everything changed at this client" className="text-ink-mid underline decoration-marine/20 underline-offset-2 hover:text-marine">
                  {change.organizationName}
                </button>
              </>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-ink-mid">
            <Icon className="h-3.5 w-3.5 text-marine" aria-hidden="true" />
            <button type="button" onClick={() => onActor(change)} title="Everything this person changed" className="text-ink underline decoration-marine/30 underline-offset-2 hover:text-marine">
              {actorName(change)}
            </button>
            <span>· {ACTOR_KIND_LABEL[change.actor.kind]}</span>
            {change.actor.demoOpenedBy && (
              <span className="inline-flex items-center gap-1">
                · <Bot className="h-3 w-3" aria-hidden="true" /> in a demo session opened by {change.actor.demoOpenedBy.replace(/^admin:/, '')}
              </span>
            )}
            <span>
              · {fields} {fields === 1 ? 'field' : 'fields'}
            </span>
          </div>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 pl-[8.5rem]">
          <table className="w-full table-fixed text-left text-[11px]">
            <thead>
              <tr className="border-b border-marine/15 text-[10px] uppercase tracking-wide text-ink-mid">
                <th className="w-1/3 py-1 pr-3 font-bold">Field</th>
                <th className="w-1/3 py-1 pr-3 font-bold">Before</th>
                <th className="w-1/3 py-1 font-bold">After</th>
              </tr>
            </thead>
            <tbody>
              {change.changes.map((field) => (
                <tr key={field.path} className="border-b border-marine/10 align-top">
                  <td className="break-words py-1 pr-3 font-mono text-ink">{field.path}</td>
                  {field.redacted ? (
                    <td colSpan={2} className="py-1 italic text-ink-mid">
                      Changed (never stored)
                    </td>
                  ) : (
                    <>
                      <td className="break-words py-1 pr-3 text-ink-mid">{formatValue(field.before)}</td>
                      <td className="break-words py-1 text-ink">{formatValue(field.after)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {change.truncated && <div className="mt-1 text-[11px] text-ink-mid">Only the first 300 fields of this change are kept.</div>}
          <div className="mt-2 text-[11px] text-ink-mid">
            {change.request ? (
              <>
                Made through <span className="font-mono">{change.request.method} {change.request.path}</span>
              </>
            ) : (
              'Made with no request behind it (a scheduled task, the background delete or a script).'
            )}
          </div>
        </div>
      )}
    </li>
  )
}
